/**
 * The inside of a LunaPin window, whichever kind it is.
 *
 * Three kinds are built today — the bookmark list, settings and the bookmark editor — and
 * this is the seam the rest arrive through: a kind is a case in `WindowContent` and a
 * branch in [contentFor]. It is a `when` over a sealed interface so that adding the case
 * without adding the branch is a compile error rather than a blank window.
 *
 * A registry rather than a factory because the toolkit asks for a window's content on
 * every rerender — a focus change, a drag, a theme flip — and a factory would rebuild
 * the DOM each time. The toolkit caches what it is given, but the *view model and its
 * collection* are ours to keep, and a fresh one per rerender would mean a new
 * subscription per paint.
 *
 * The list view itself has no state, no fetch and no branching of its own: it renders one
 * `BookmarkListBackingViewModel.State` and nothing else, so an Android or iOS list
 * renders the same three states from the same flow.
 *
 * @see se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel
 * @see BookmarkListView
 */
package se.soderbjorn.lunapin.web

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import kotlin.js.Date
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.viewmodel.BookmarkListBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.EditBookmarkBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.SettingsBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.WindowContent

/**
 * One window's view and the work behind it.
 *
 * @property root what the toolkit mounts into its window body.
 * @property job **everything this window has running**, as one job, cancelled when the
 *   window closes — or null for a kind with nothing running behind it. No kind is null
 *   today: settings was, while it was only the bookmarklet and could be derived from the
 *   page it was running on, and LPN-17 gave it a view model like the other two. The case is
 *   kept because the next kind may well have nothing behind it, and null is the honest
 *   answer for one — better than an inert [Job] nobody ever completes, which would be a
 *   live object pretending to be work.
 *
 *   *Everything* is load-bearing and was not always true. This used to hold only the flow
 *   collection, while each view model's own `run` was launched loose into the shared scope
 *   — harmless while `run` did nothing but wait to be asked, and a real leak the moment one
 *   of them grew a timer. A window that closes must stop asking the server, so both are
 *   launched as children of one job and the window's teardown is that job's cancellation.
 * @property hasUnsavedChanges whether closing this window now would throw something typed
 *   away. Asked at the moment of the gesture rather than pushed on every state, because
 *   the answer is only ever wanted once and only by whoever is about to close it.
 *
 *   A lambda per window rather than a flag on the class, so a kind that cannot lose
 *   anything — a list, settings — is the default and says nothing. Only the editor
 *   overrides it, which is also the only kind holding a form.
 * @property onShellRefreshed what this window has to redo after the toolkit rebuilds its
 *   pane subtrees. A lambda for [hasUnsavedChanges]' reason — a kind with nothing to redo
 *   is the default and says nothing.
 *
 *   Only the list overrides it, and what it redoes is its scroll position: re-tiling moves
 *   the scroller, and a moved scroller is a scroller the browser has reset. See
 *   `BookmarkListView.onShellRefreshed`.
 */
private class OpenWindow(
    val root: HTMLElement,
    val job: Job?,
    val hasUnsavedChanges: () -> Boolean = { false },
    val onShellRefreshed: () -> Unit = {},
    val list: BookmarkListBackingViewModel? = null,
)

/**
 * Builds and owns the body of every open window.
 *
 * A registry rather than a factory because the toolkit asks for a window's content on
 * every rerender — a focus change, a drag, a theme flip — and a factory would rebuild
 * the DOM each time. The toolkit caches what it is given, but the *view model and its
 * collection* are ours to keep, and a fresh one per rerender would mean a new
 * subscription per paint.
 *
 * @param storage handed to each window's view model.
 * @param scope the scope each window's collection is launched on.
 * @param onCloseWindow what a window asks for when it wants to close itself — the editor's
 *   Cancel, its `Esc`, and the close after a delete. Passed in rather than reached for,
 *   because closing a window is the workspace's business and this registry only owns
 *   bodies. The caller is expected to [dispose] of the window as part of answering.
 * @param onLabelChanged a window's name has changed and the shell should ask for pane
 *   labels again. Only the editor ever calls it, and only when a bookmark's title arrives
 *   or is edited.
 * @param onEditBookmark a row in a list window asked for its bookmark to be opened for
 *   editing. Whether that makes a window or focuses one is the workspace's rule, so it is
 *   asked rather than decided here.
 * @param isEventStreamConnected whether the push channel is live. Handed to every list
 *   window, which is the only surface that says anything about it — see `BookmarkListView`.
 * @param onFilterChanged a list window's filter changed. The filter *is* the window, so it
 *   belongs in the stored workspace rather than in a view model that dies with the body —
 *   and the caller is expected to be lazy about how often it writes, since this fires on
 *   every keystroke in the search field.
 */
class WindowBodies(
    private val storage: StorageRepository,
    private val scope: CoroutineScope,
    private val onCloseWindow: (String) -> Unit = {},
    private val onLabelChanged: () -> Unit = {},
    private val onEditBookmark: (Long) -> Unit = {},
    private val onFilterChanged: (String, WindowContent.BookmarkList) -> Unit = { _, _ -> },
    private val isEventStreamConnected: () -> Boolean = { true },
) {
    private val windows: MutableMap<String, OpenWindow> = mutableMapOf()

    /**
     * What each window is called, for the windows that have anything to say.
     *
     * A map beside [windows] rather than a field on [OpenWindow], because it is written
     * from inside the window's own construction — before `getOrPut` has put anything in
     * that map — and a field would have to be reached through an entry that does not exist
     * yet.
     */
    private val labels: MutableMap<String, String> = mutableMapOf()

    /**
     * The body element for [windowId], built on first ask and returned unchanged
     * afterwards.
     *
     * @param content what to build if this is the first ask. Ignored afterwards, and
     *   that is correct rather than lazy: a window does not change kind, so a cached
     *   body is always the right kind.
     *
     *   [WindowContent.EditBookmark] is the case that was expected to break that, and it
     *   does not — because a window's bookmark never changes either. There is no intent
     *   that repoints an editor; opening a different bookmark opens a different window,
     *   which is a different id and therefore a different entry here. `WindowContent`'s
     *   own doc for that case states it as the invariant this line depends on, so that
     *   the day somebody wants a repointable editor they find out here first.
     *
     *   [WindowContent.BookmarkList]'s content *does* change afterwards — LPN-16 gave it a
     *   filter, and every knob rewrites it — and ignoring it afterwards is still right,
     *   because the change **comes from this body**. The filter travels outwards from the
     *   window's view model, through `onFilterChanged`, into the workspace; nothing pushes
     *   it back the other way, and a cache that re-read it would be reading its own echo.
     *   Only the value handed over on the *first* ask matters, and that one is the stored
     *   filter a reload restores.
     */
    fun contentFor(windowId: String, content: WindowContent): HTMLElement =
        windows.getOrPut(windowId) {
            when (content) {
                is WindowContent.BookmarkList -> buildBookmarkList(windowId, content)
                WindowContent.Settings -> buildSettings()
                is WindowContent.EditBookmark -> buildEditBookmark(windowId, content.bookmarkId)
            }
        }.root

    /**
     * What the sidebar and the pane header should call [windowId], or null for a window
     * with nothing better to say than its number.
     *
     * Only an editor answers, and only once its bookmark has arrived — the shell asks for a
     * label synchronously and cannot wait for a fetch, so the honest answer before then is
     * "nothing", and the caller falls back to a neutral name. When the answer changes, the
     * window says so through `onLabelChanged` and the shell asks again.
     */
    fun label(windowId: String): String? = labels[windowId]

    /**
     * Whether closing [windowId] right now would lose something typed into it.
     *
     * What the workspace asks before it lets a close through, and the whole of why the
     * toolkit's own close confirmation is turned off — see `confirmPaneClose` in `main.kt`.
     * A window that is not open, or one of a kind that holds no form, answers `false`, so
     * the caller's question is always answerable and the quiet path is the default one.
     */
    fun hasUnsavedChanges(windowId: String): Boolean =
        windows[windowId]?.hasUnsavedChanges?.invoke() ?: false

    /**
     * Tell every open window that the toolkit has just rerendered.
     *
     * Wired to `AppShellSpec.onAfterRefresh`, which fires at the end of every rerender pass
     * — after the toolkit has rebuilt its chrome and, crucially, after `LayoutRenderer` has
     * rebuilt the pane subtrees. That rebuild moves each window's body in the DOM, and a
     * scroller that moves is a scroller the browser has silently reset to the top.
     *
     * Broadcast rather than aimed at one window, because the pass rebuilds all of them and
     * the hook says nothing about which. Every kind but the list ignores it.
     */
    fun onShellRefreshed() {
        windows.values.forEach { it.onShellRefreshed() }
    }

    /**
     * [windowId]'s live filter, or null when it is not an open list window.
     *
     * Live rather than stored, for `BookmarkListBackingViewModel.filter`'s reason: the
     * workspace's copy waits for the typing to pause, and the Starred dialog asks "is what
     * this window shows starred?" about what it shows *now*.
     */
    fun listFilter(windowId: String): WindowContent.BookmarkList? = windows[windowId]?.list?.filter()

    /**
     * Show [filter] in [windowId] — a starred filter was picked (LPN-68). The window keeps
     * its column count; the change travels outwards through `onFilterChanged` like any knob.
     */
    fun replaceFilter(windowId: String, filter: WindowContent.BookmarkList) {
        windows[windowId]?.list?.onFilterReplaced(filter)
    }

    /**
     * Forget [windowId] and stop collecting for it.
     *
     * Called when the window closes. Without it, every window ever opened keeps a live
     * collector on the session flow for the lifetime of the page — which is invisible
     * until somebody opens and closes windows for a while and the app slows down for
     * no reason a profiler makes obvious.
     */
    fun dispose(windowId: String) {
        windows.remove(windowId)?.job?.cancel()
        labels.remove(windowId)
    }

    /**
     * The collection, filtered — which is to say, a saved filter over it.
     *
     * A view model per window rather than one shared by all of them. Two lists open at
     * once are two requests, which looks wasteful and is the honest shape: they can be
     * refreshed independently, and a shared model would have to decide what happens
     * when one window refreshes and the other is mid-render. Now that these carry filters
     * that is not merely honest but forced — per-window state is the only thing that works
     * at all when two windows in one tab show different filters.
     *
     * The clock and the zone are handed in rather than reached for, because `:client` has
     * neither: `Date` is the browser's, and the day headers and the *added within* window
     * are the two things that need one. Read on every call so a window left open across
     * midnight, or a laptop carried across a time zone, is right afterwards.
     */
    private fun buildBookmarkList(windowId: String, filter: WindowContent.BookmarkList): OpenWindow {
        val viewModel = BookmarkListBackingViewModel(
            storage = storage,
            now = { Date.now().toLong() },
            // `getTimezoneOffset` is minutes *behind* UTC — positive west of Greenwich —
            // which is the opposite sign from the offset everything else in the world means
            // by it. Negated here, once, so `BookmarkFilter` can take the ordinary one.
            zoneOffsetMillis = { -Date().getTimezoneOffset() * 60 * 1000 },
            initialFilter = filter,
            onFilterChanged = { onFilterChanged(windowId, it) },
            // *Try again* is launched here rather than inside `run`, so that pressing it and
            // then closing the window still finishes the request the user asked for — the
            // same split `EditBookmarkBackingViewModel` makes for a save.
            scope = scope,
        )
        val view = BookmarkListView(
            model = viewModel,
            onEditBookmark = onEditBookmark,
            isEventStreamConnected = isEventStreamConnected,
        )
        // One job holding both children. `run` now carries the refresh-while-waiting timer,
        // so a window that closed while it was armed would keep asking the server forever
        // if this were still two loose launches — see [OpenWindow.job].
        val job = scope.launch {
            launch { viewModel.stateFlow.collect { view.render(it) } }
            launch { viewModel.run() }
        }
        return OpenWindow(view.root, job, onShellRefreshed = view::onShellRefreshed, list = viewModel)
    }

    /**
     * The settings window.
     *
     * **This used to be the kind with no job**, and the line that changed is the one
     * [OpenWindow.job] predicted: settings was "derived from the page it is running on"
     * while it was only the bookmarklet, and it is now a surface that loads what the account
     * has chosen, writes every change back, asks the provider whether the key works and
     * re-reads the month's usage after a backfill. All of that is work behind a window, and
     * all of it stops when the window closes — which is the property LPN-13 fixed here and
     * which a new kind must not quietly reintroduce a hole in.
     *
     * No window id is needed: settings has no name to report, nothing to close itself for,
     * and only one of it can be open — the workspace focuses the existing window rather than
     * making a second.
     */
    private fun buildSettings(): OpenWindow {
        val viewModel = SettingsBackingViewModel(
            storage = storage,
            // Writes, the backfill and the connection test are launched here rather than
            // inside `run`, so flicking a switch and closing the window still finishes the
            // write — the same split the editor makes for a save, and the reason a settings
            // screen with no Save button can be trusted at all.
            scope = scope,
        )
        val view = SettingsView(viewModel)
        // One job holding both children, so the window's teardown is that job's cancellation
        // — see [OpenWindow.job].
        val job = scope.launch {
            launch { viewModel.stateFlow.collect { view.render(it) } }
            launch { viewModel.run() }
        }
        return OpenWindow(view.root, job)
    }

    /**
     * One bookmark, open for changing.
     *
     * A view model per window, like the list — and here it is not merely tidy but load
     * bearing: two editors are two different bookmarks with two forms, two in-flight saves
     * and two answers to "is this one gone?". There is nothing for a shared model to share.
     *
     * The view is built before the collection starts, so the first state has somewhere to
     * land; and the window's own id is closed over rather than looked up, which is how the
     * body can ask to be closed without knowing which tab it is sitting in.
     */
    private fun buildEditBookmark(windowId: String, bookmarkId: Long): OpenWindow {
        val viewModel = EditBookmarkBackingViewModel(bookmarkId, storage, scope)
        val view = EditBookmarkView(
            model = viewModel,
            onClose = { onCloseWindow(windowId) },
            onTitleChanged = { title ->
                if (labels[windowId] != title) {
                    if (title == null) labels.remove(windowId) else labels[windowId] = title
                    onLabelChanged()
                }
            },
        )
        // One job holding both children, for the list's reason — see [OpenWindow.job].
        val job = scope.launch {
            launch {
                viewModel.stateFlow.collect { state ->
                    view.render(state)
                    // The one state that answers itself: the user pressed Delete and
                    // confirmed it, so a panel explaining what they just did would be
                    // furniture. A bookmark that went somewhere *else* keeps its window and
                    // gets the panel.
                    if (state.gone?.deletedHere == true) onCloseWindow(windowId)
                }
            }
            launch { viewModel.run() }
        }
        // Read off the view model's current state at the moment somebody tries to close the
        // window, rather than tracked here as the states go by: the model already knows the
        // answer — see `State.Ready.hasUnsavedChanges` — and a copy kept in this class would
        // be a second version of it that can be stale.
        //
        // Named, not a trailing lambda: a trailing lambda binds to OpenWindow's *last*
        // parameter, which made this the editor's `onShellRefreshed` and left
        // `hasUnsavedChanges` at its `false` default.
        return OpenWindow(
            view.root,
            job,
            hasUnsavedChanges = { viewModel.stateFlow.value.ready?.hasUnsavedChanges == true },
        )
    }
}
